import { useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { queryClient } from "../lib/queryClient";
import { queryKeys } from "../lib/queryKeys";
import { invalidateSharingQueries } from "../lib/invalidateSharing";
import { Task } from "../types";

/**
 * Subscribes to Supabase Realtime for tasks, lists, tags, and filters.
 * On any event, invalidates the relevant TanStack Query cache so every
 * consumer sees fresh data from the server — including changes made on
 * other devices or by server-side processes (e.g., Google Calendar sync).
 *
 * Previously, the app's single realtime handler only propagated three
 * fields (scheduled_date, scheduled_time, duration) from UPDATE events.
 * Title, completion state, tag changes, list membership, etc. were silently
 * dropped, leaving multi-device users with stale data.
 *
 * The tasks and lists channels carry no `user_id` filter: with shared Lists
 * (#286, ADR-0001), a Member needs to hear about a change to a Task or List
 * they don't own too. Realtime applies each table's own SELECT policy per
 * subscriber, so dropping the filter doesn't widen who actually receives an
 * event — it just stops narrowing it below what the database already
 * allows. Tags stay personal (no change) and task_filters/task_captures
 * aren't shared at all, so those three keep their owner-only filter.
 */
export function useRealtimeSync() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const tasksChannel = supabase
      .channel("realtime-tasks-v2")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "tasks",
        },
        () => {
          queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "tasks",
        },
        () => {
          queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "tasks",
        },
        (payload) => {
          const deletedId = (payload.old as { id?: string }).id;
          if (deletedId) {
            // Left alone when the cache has not loaded: writing `[]` into it
            // would flip useTasks out of its pending state and drop the app's
            // loading gate. The invalidation below is what brings the post-
            // delete list in. Same guard the mutation hooks' optimistic
            // updaters use.
            queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
              prev ? prev.filter((t) => t.id !== deletedId) : prev
            );
          }
          queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
        }
      )
      .subscribe();

    const listsChannel = supabase
      .channel("realtime-lists")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "lists",
        },
        () => {
          queryClient.invalidateQueries({ queryKey: queryKeys.lists });
        }
      )
      .subscribe();

    // Shared lists (#286): a new Invitation, an acceptance, or a Leave/
    // Remove/Unshare all land here. No user_id filter, same reasoning as
    // tasks/lists above — RLS already narrows each subscriber to the rows
    // they're allowed to see (every row on a List they own, plus their own
    // row elsewhere), so an Owner's manage panel and a Member's board both
    // hear about it without waiting for the next page load.
    const listMembersChannel = supabase
      .channel("realtime-list-members")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "list_members",
        },
        () => {
          invalidateSharingQueries(queryClient);
        }
      )
      .subscribe();

    const tagsChannel = supabase
      .channel("realtime-tags")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "tags",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: queryKeys.tags });
        }
      )
      .subscribe();

    const filtersChannel = supabase
      .channel("realtime-filters")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "task_filters",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: queryKeys.filters });
        }
      )
      .subscribe();

    const capturesChannel = supabase
      .channel("realtime-task-captures")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "task_captures",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: queryKeys.taskCaptures });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(tasksChannel);
      supabase.removeChannel(listsChannel);
      supabase.removeChannel(listMembersChannel);
      supabase.removeChannel(tagsChannel);
      supabase.removeChannel(filtersChannel);
      supabase.removeChannel(capturesChannel);
    };
  }, [user]);
}
